<template>
    <div class="my-6">
        <div>
            <div class="flex justify-between w-full pb-1">
                <h3 class="text-xl font-medium leading-5 text-gray-900">
                    Distributors
                </h3>
            </div>
            <div
                v-if="currentDistributors.length > 0"
                class="block gap-4 mt-4 md:grid md:grid-cols-4 lg:grid-cols-5"
            >
                <div
                    v-for="(distributor) in currentDistributors"
                    :key="distributor.id"
                    class="flex-wrap text-left bg-white border border-gray-300 rounded-lg shadow md:col-span-1 md:text-center md:grid md:grid-rows-5 md:max-h-72"
                >
                    <button
                        @click="openDeleteModal(distributor)"
                        class="self-start block row-span-1 mt-2 mr-2 text-red-600 rounded-full md:max-h-4 justify-self-end md:bg-white md:rounded-none"
                    >
                        <span class="inline-flex text-xs md:sm">
                            Remove
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="inline-flex w-4 h-4 text-red-600 md:h-5 md:w-5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                    </button>
                    <a
                        :href="getRoute(distributor)"
                        class="block hover:bg-gray-50"
                    >
                        <div class="flex my-2 md:flex-col md:flex-1 md:row-span-4 md:my-0">
                            <img :src="distributor.avatar" :alt="distributor.name" class="w-10 h-10 ml-2 rounded-full shadow md:h-20 md:w-20 md:ml-0"/>
                            <h3 class="ml-2 font-medium text-gray-600 md:ml-0 md:mt-3 text-md md:text-sm">{{ distributor.name }}</h3>
                            <dl class="flex-col flex-grow hidden mt-1 md:flex">
                                <dd class="text-sm text-gray-500">{{ distributor.city }}, {{ distributor.state }}</dd>
                                <dd class="mt-1">
                                    <span class="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">{{ distributor.distributorship_name }}</span>
                                </dd>
                            </dl>
                        </div>
                        <div class="flex mb-2 md:hidden">
                            <dd class="inline-block ml-2 text-sm text-gray-500">{{ distributor.city }}, {{ distributor.state }}</dd>
                            <dd class="inline-block ml-2">
                                <span class="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">{{ distributor.distributorship_name }}</span>
                            </dd>
                        </div>
                    </a>
                </div>
                <div class="flex items-center flex-shrink-0 col-span-1 mt-2 justify-self-center md:justify-self-start md:mt-0">
                    <button
                        v-if="currentDistributors.length > 0"
                        @click="openAddDistributorModal()"
                        class="w-10 h-10 text-center bg-green-600 rounded-full md:w-12 md:h-12 hover:bg-green-700 justify-items-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10 text-center text-white">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                    </button>
                </div>
            </div>
            <div v-else class="mt-4">
                <div class="p-6 text-center border-2 border-gray-200 border-dashed rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 mx-auto text-gray-400">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                    </svg>
                    <h3 class="mt-2 text-lg font-medium text-gray-900">You haven't added any distributors for this profile yet!</h3>
                    <p class="mt-1 text-gray-500 text-md">These connections are for your eyes only! Keep that rolodex up to date!</p>
                    <p class="mt-1 text-gray-500 text-md">Click below to link distributors to this profile.</p>
                    <div class="mt-6">
                        <button
                            class="tw-button-green"
                            @click="openAddDistributorModal()"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5 mr-1 text-white">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Add distributor
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <profile-suggested-distributors
            @add-to-profile="addSuggestedDistributor"
            :search-filters="searchFilters"
            :search-route="suggestedSearchRoute"
            :model="model"
            :distributors="currentDistributors"
        >
        </profile-suggested-distributors>
        <tw-modal ref="add_distributor_modal">
            <template v-slot:content>
                <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                    <div class="w-full sm:flex sm:items-start">
                        <div class="flex items-center justify-center flex-shrink-0 p-2 mx-auto bg-gray-100 rounded-full w-14 h-14 sm:mx-0 sm:h-10 sm:w-10">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10 mx-auto text-gray-400">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                            </svg>
                        </div>
                        <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 class="text-lg leading-6 text-gray-600">
                                Add a distributor
                            </h3>
                        </div>
                    </div>
                    <div class="w-full mt-3">
                        <div
                            v-if="selectedDistributors.length > 0"
                            class="my-1"
                        >
                            <span
                                v-for="(distributor, index) in selectedDistributors"
                                :key="distributor.id"
                                class="inline-flex items-center py-0.5 pl-2 pr-2 rounded-full text-xxs font-medium bg-blue-100 text-blue-800 truncate"
                            >
                                {{ distributor.name }}
                                <button
                                    type="button"
                                    class="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-500 focus:text-white"
                                    @click="removeDistributor(index, value)"
                                >
                                    <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                        <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
                                    </svg>
                                </button>
                            </span>
                        </div>
                        <input
                            v-debounce:400ms="updateSearch"
                            type="search"
                            id="elastic-search-input"
                            @search="searchClear"
                            placeholder="Search distributors"
                            class="block w-full p-2 pl-8 text-xs border-gray-300 rounded-md shadow focus:border-blue-500 focus:ring-0 md:p-4 md:pl-12 sm:p-3 sm:leading-5 sm:text-sm"
                        />
                        <ul
                            v-if="searchResults.length > 0"
                            class="mt-2 overflow-y-scroll border-gray-200 rounded-lg border-1 max-h-44"
                        >
                            <li
                                v-for="(distributor) in searchResults"
                                :key="distributor.id"
                            >
                                <a
                                    @click="selectDistributor(distributor)"
                                    class="block transition duration-150 ease-in-out hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                                >
                                    <div class="flex items-center px-4 py-4 sm:px-6">
                                        <div class="flex items-center flex-1 min-w-0">
                                            <div
                                                class="flex-1 min-w-0 md:px-4 md:grid md:grid-cols-4 md:gap-4"
                                            >
                                                <div class="col-span-4 space-y-1">
                                                    <div
                                                        class="flex space-x-2 text-sm font-semibold leading-5 text-blue-900 truncate hover:text-blue-700 visited:text-indigo-800"
                                                    >
                                                        <span
                                                            class="flex-shrink-0 truncate lg:flex-shrink"
                                                            v-html="distributor.name_edge_ngram"
                                                        >
                                                            {{ distributor.name }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-if="distributor.city_state_edge_ngram"
                                                        class="flex items-center text-sm leading-5 text-gray-500 sm:mt-0"
                                                    >
                                                        <svg
                                                            class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                            fill-rule="evenodd"
                                                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                                            clip-rule="evenodd"
                                                            />
                                                        </svg>
                                                        <span v-html="distributor.city_state_edge_ngram"></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
                    <button
                        class="tw-button-gray"
                        @click="closeAddDistributorModal"
                    >
                        Cancel
                    </button>
                    <button
                        class="tw-button-green"
                        @click="addDistributor()"
                    >
                        Add
                    </button>
                </div>
            </template>
        </tw-modal>
        <tw-modal ref="delete_distributor_modal">
            <template v-slot:content>
            <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                    <div class="w-full sm:flex sm:items-start">
                        <div class="flex items-center justify-center flex-shrink-0 p-2 mx-auto bg-red-100 rounded-full w-14 h-14 sm:mx-0 sm:h-10 sm:w-10">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-red-600">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                        </div>
                        <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 class="text-lg leading-6 text-gray-600">
                                Are you sure you want to remove this distributor?
                            </h3>
                        </div>
                    </div>
                </div>
                <div class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
                    <button
                        class="tw-button-gray"
                        @click="closeDeleteModal"
                    >
                        Cancel
                    </button>
                    <button
                        class="tw-button-red"
                        @click="deleteDistributor(removedDistributor)"
                    >
                        Remove
                    </button>
                </div>
            </template>
        </tw-modal>
    </div>
</template>
<script>
import { getDirective } from 'vue-debounce';
export default {
    directives: {
        debounce: getDirective()
    },
    props: {
        distributors: {
            type: [Array, Object],
            required: false,
            default: []
        },
        submitRoute: {
            type: String,
            required: true,
        },
        removeRoute: {
            type: String,
            required: true,
        },
        searchFilters: {
            type: [Array, Object],
            required: true,
        },
        suggestedSearchRoute: {
            type: String,
            required: true,
        },
        model: {
            type: [Array, Object],
            required: true,
        }
    },
    data() {
        return {
            isLoading: false,
            selectedDistributors: [],
            currentDistributors: this.distributors,
            searchRoute: this.route('search.filtered_results', 'distributors'),
            serverParams: {
                searchTerm: '',
                filters: {},
                pageSize: 100,
                page: 0,
            },
            searchResults: {},
            removedDistributor: {},
        }
    },
    methods: {
        getRoute(rep) {
            return this.route('distributors.show', { distributor: rep.id});
        },
        openAddDistributorModal() {
            this.$refs.add_distributor_modal.show();
        },
        async loadItems() {
            this.isLoading = true;
            try {
                const response = await axios.get(this.searchRoute, {params: this.serverParams})
                this.searchResults = response.data.results;
                this.isLoading = false;
            } catch(error) {
                this.searchResults = {},
                this.isLoading = false;
                return false;
            }

            return true;
        },
        updateSearch(val, e) {
            this.serverParams.page = 0;
            this.serverParams.searchTerm = val;
            this.loadItems();
        },
        searchClear() {
            if(document.getElementById('elastic-search-input').value === '') {
                this.serverParams.searchTerm = document.getElementById('elastic-search-input').value;
                this.searchResults = {};
            }
        },
        selectDistributor(distributor) {
            distributor.name = distributor.name.replace(/<\/?[^>]+(>|$)/g, "");
            this.selectedDistributors.push(distributor);
        },
        closeAddDistributorModal() {
            this.$refs.add_distributor_modal.hide();
            this.searchResults = {};
            this.selectedDistributors = [];
        },
        removeDistributor(index) {
            this.selectedDistributors.splice(index, 1);
        },
        async addDistributor() {
            const ids = this.selectedDistributors.map(distributor => distributor.id);
            const result = await axios.get(this.submitRoute, {'params' : ids});
            this.currentDistributors = result.data;
            this.searchResults = {};
            this.selectedDistributors = [];
            this.closeAddDistributorModal();
        },
        async deleteDistributor(distributor) {
            const result = await axios.get(this.removeRoute, {'params': {'distributor': distributor.id}})
            this.currentDistributors = result.data;
            this.closeDeleteModal();
            this.removedDistributor = {};
        },
        openDeleteModal(distributor) {
            this.$refs.delete_distributor_modal.show();
            this.removedDistributor = distributor;
        },
        closeDeleteModal() {
            this.$refs.delete_distributor_modal.hide();
            this.removedDistributor = {};
        },
        async addSuggestedDistributor(distributor_id) {
            const result = await axios.get(this.submitRoute, {'params': [distributor_id]});
            this.currentDistributors = result.data;
        }
    }
}
</script>
